import React, { ReactNode } from 'react';

import { BodyText, Box } from '../../atoms';
import { Watermark } from './components';
import {
  Card,
  WatermarkContainer,
  NumbersContainer,
  Number,
  AddressContainer,
  Content,
  Row,
} from './BankAccount.styles';

import { formatAddressDetails } from './utils';
import { AccountDetails, AddressDetails, Size, Variant, Status } from './types';
import { defaultTranslations } from './constants';

export type BankAccountProps = {
  /**
   * Bank account details
   */
  accountDetails: AccountDetails;
  /**
   * Address details
   */
  addressDetails?: AddressDetails;
  /**
   * Component size
   */
  size?: Size;
  /**
   * Component variant
   */
  variant?: Variant;
  /**
   * Component translation object
   */
  translations?: Record<keyof typeof defaultTranslations, string>;
  /**
   * Component status
   */
  status?: Status;
  /**
   * Component render slot function
   */
  renderSlot?: () => ReactNode;
};

/**
- Bank account component used for displaying bank account details.
**/
const BankAccount = ({
  accountDetails,
  addressDetails = {},
  size = 'regular',
  variant = 'primary',
  translations = defaultTranslations,
  status,
  renderSlot,
}: BankAccountProps) => {
  const { firstName, lastName, bankName, routingNumber, accountNumber } =
    accountDetails;

  const address = formatAddressDetails(addressDetails);
  const hasAddress = !!address.length;

  return (
    <Card cardSize={size} variant={variant} status={status}>
      <WatermarkContainer size={size}>
        <Watermark />
      </WatermarkContainer>
      <Content contentSize={`${size}-${hasAddress}`}>
        <Row>
          <BodyText variant="body1" overflow="truncate">
            {bankName}
          </BodyText>
          {renderSlot && (
            <div style={{ marginLeft: 'auto' }}>{renderSlot()}</div>
          )}
        </Row>
        <Box marginTop="auto" marginBottom={3}>
          <BodyText variant="body2" overflow="truncate">
            {firstName} {lastName}
          </BodyText>
        </Box>
        <NumbersContainer gridGap={8}>
          <Number>
            <BodyText variant="body3" color="gray.dark" overflow="truncate">
              {translations.routing_number}
            </BodyText>
            <BodyText variant="body2" overflow="truncate">
              {routingNumber}
            </BodyText>
          </Number>
          <Number>
            <BodyText variant="body3" color="gray.dark" overflow="truncate">
              {translations.account_number}
            </BodyText>
            <BodyText variant="body2" overflow="truncate">
              {accountNumber}
            </BodyText>
          </Number>
        </NumbersContainer>
      </Content>
      {hasAddress && (
        <AddressContainer addressSize={size}>
          <BodyText variant="body3" color="gray.dark" overflow="truncate">
            {address}
          </BodyText>
        </AddressContainer>
      )}
    </Card>
  );
};

export default BankAccount;
